<template>
    <nav
        class="navbar"
        :style="computedStyle"
        role="navigation"
        aria-label="main navigation">
        <div class="navbar-brand">
            <router-link
                class="navbar-item"
                :to="{ name: 'FrontPage' }">
                <img
                    alt="Common Library logo"
                    src="/images/logo_light_white_inside.png">
                <p class="title is-5 ml-3 is-hidden-desktop">
                    Common Library
                </p>
            </router-link>
            <a
                role="button"
                class="navbar-burger"
                aria-label="menu"
                aria-expanded="false"
                @click="toggleNavbarMenu">
                <span aria-hidden="true" />
                <span aria-hidden="true" />
                <span aria-hidden="true" />
            </a>
        </div>
        <div
            v-if="!loading"
            class="navbar-menu"
            :class="{ 'is-active': isNavbarMenuExpanded }">
            <top-menu />
            <!--<top-menu v-if="hasCommonLibraryAccess" />-->
            <div class="navbar-end">
                <div class="navbar-item is-flex is-gap-10">
                    <template v-if="user">
                        <b-dropdown>
                            <template #trigger>
                                <button class="button is-dropdown-trigger">
                                    <b>{{ user.name }}</b>
                                </button>
                            </template>
                            <b-dropdown-item custom>
                                <b>Assigned roles:</b>
                            </b-dropdown-item>
                            <b-dropdown-item
                                v-for="(role, idx) in roles"
                                :key="`${role}-${idx}`"
                                custom>
                                {{ role }}
                            </b-dropdown-item>
                            <b-dropdown-item custom>
                                <b>Access groups:</b>
                            </b-dropdown-item>
                            <b-dropdown-item
                                v-for="(accessGroup, idx) in accessGroups"
                                :key="`${accessGroup}-${idx}`"
                                custom>
                                {{ accessGroup }}
                            </b-dropdown-item>
                            <b-dropdown-item separator />
                            <b-dropdown-item
                                @click="() => $router.push('/UserProfile')">
                                Profile
                                <!--TODO remove when buefy's use of <a> tags in dropdown items is fixed-->
                            </b-dropdown-item>
                            <!--<b-dropdown-item>
                                <router-link to="/UserProfile">
                                    Profile
                                </router-link>
                            </b-dropdown-item>-->
                            <b-dropdown-item
                                @click="refreshAttributes">
                                Refresh Profile
                            </b-dropdown-item>
                            <b-dropdown-item>
                                <a @click="signOut">Sign out</a>
                            </b-dropdown-item>
                        </b-dropdown>
                    </template>
                    <a
                        v-else
                        @click="signIn">Sign in</a>
                    <button
                        v-if="systemInfo.environment"
                        class="button"
                        @click="toggleSystemInfo">
                        <b-icon
                            icon="cog"
                            size="is-small" />
                        <span
                            v-if="systemInfo.environment !== 'Production'"
                            style="margin-left:0.1em;">
                            {{ systemInfo.environment }}
                        </span>
                    </button>
                </div>

                <b-modal
                    v-model="systemInfoVisible">
                    <div class="box">
                        <h2 class="title is-4">
                            Current environment information
                        </h2>
                        <table class="table is-fullwidth">
                            <tbody>
                                <tr>
                                    <td><b>API environment</b></td>
                                    <td>{{ systemInfo.environment }}</td>
                                </tr>
                                <tr>
                                    <td><b>API version</b></td>
                                    <td>{{ systemInfo.apiVersion }}</td>
                                </tr>
                                <tr>
                                    <td><b>API server name</b></td>
                                    <td>{{ systemInfo.machineName }}</td>
                                </tr>
                                <tr>
                                    <td><b>Azure resource group</b></td>
                                    <td>{{ systemInfo.resourceGroup }}</td>
                                </tr>
                                <tr>
                                    <td><b>OS</b></td>
                                    <td>{{ systemInfo.os }}</td>
                                </tr>
                                <tr v-for="s in systemInfo.settings">
                                    <td><b>{{ s.key }}</b></td>
                                    <td>{{ s.value }}</td>
                                </tr>
                            </tbody>
                        </table>
                        <hr>
                        <h2 class="title is-4">
                            Common Library environments
                        </h2>
                        <table class="table is-fullwidth">
                            <thead>
                                <tr>
                                    <th>Environment</th>
                                    <th>User application URL</th>
                                    <th>API swagger URL</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="env in environments">
                                    <td>{{ env.name }}</td>
                                    <td>
                                        <a
                                            class="underline-link"
                                            :href="env.ui"
                                            target="_blank">{{ env.ui }}</a>
                                    </td>
                                    <td>
                                        <a
                                            class="underline-link"
                                            :href="env.api"
                                            target="_blank">{{ env.api }}</a>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </b-modal>
            </div>
        </div>
    </nav>
</template>
<script>
    import TopMenu from '@/shared/components/TopMenu.vue';
    import { showMixin } from '@/shared/mixins/showMixin';
    import { getSystemInfo } from '@/shared/helpers/api';

    export default {
        components: { TopMenu },
        mixins: [showMixin],
        data() {
            return {
                loading: true,
                roles: [],
                user: null,
                accessGroups: [],
                systemInfoVisible: false,
                systemInfo: {},
                backgroundColor: 'white',
                isNavbarMenuExpanded: false,
                environments: [
                    {
                        name: 'Production',
                        ui: 'https://commonlib.equinor.com',
                        api: 'https://commonlibapi.equinor.com'
                    },
                    {
                        name: 'QA',
                        ui: 'https://qa-commonlib.equinor.com/',
                        api: 'https://qa-commonlibapi.equinor.com/'
                    },
                    {
                        name: 'QA2',
                        ui: 'https://cl-qa2-app-ui.azurewebsites.net/',
                        api: 'https://cl-qa2-app-api.azurewebsites.net'
                    },
                    {
                        name: 'Development',
                        ui: 'https://cl-dev-app-ui.azurewebsites.net/',
                        api: 'https://cl-dev-app-api.azurewebsites.net/'
                    }
                ]
            };
        },
        computed: {
            computedStyle() {
                return `border-bottom: 1px solid #DBDBDB; background-color: ${this.backgroundColor}`;
            },
            hasCommonLibraryAccess() {
                return window.authService.hasAnyCommonLibraryRole();
            }
        },
        async mounted() {
            await window.authService.redirectHandled();

            this.user = window.authService.getUser();
            this.roles = this.getRolesToShow(this.user);

            await Promise.all([
                window.authService.getAccessGroups().then(accessGroups => {
                    this.accessGroups = accessGroups;
                }),
                getSystemInfo(this).then(systemInfo => {
                    this.systemInfo = systemInfo;
                    this.backgroundColor = this.navbarColorFromEnvironment(systemInfo.environment);
                })
            ]);

            this.loading = false;
        },
        methods: {
            async signIn() {
                await window.authService.signIn();
            },
            async refreshAttributes() {
                await window.authService.refreshUserAttributes();
            },
            /**
             * Returns the list of important roles to show in User menu.
             *
             * @param user : {AccountInfo}
             * @returns {string[]}
             */
            getRolesToShow(user) {
                const roleFilter = ['Administrator', 'CodeEditor', 'LibraryEditor', 'Viewer'];
                return (user?.idTokenClaims?.roles ?? []).filter(roles => roleFilter.includes(roles));
            },
            async signOut() {
                await window.authService.signOut();
            },
            navbarColorFromEnvironment(env) {
                if (env === 'Development') {
                    return '#ff9ed9';
                } else if (env === 'dev') {
                    return '#fdee92';
                } else if (env === 'QA' || env === 'qa') { // Can be cleaned up after azure migration
                    return '#a190de';
                } else if (env === 'QA2' || env === 'qa2') { // Can be cleaned up after azure migration
                    return '#ff9200';
                } else {
                    return 'white';
                }
            },
            toggleSystemInfo() {
                this.systemInfoVisible = !this.systemInfoVisible;
            },
            toggleNavbarMenu() {
                this.isNavbarMenuExpanded = !this.isNavbarMenuExpanded;
            }
        }
    };
</script>

<style scoped>
.underline-link {
    text-decoration: underline;
    color: #007079;
    font-weight: normal;
}
</style>
